/**
 * Ocean Booking Locations Fields Component
 *
 * Renders the shipment locations section of the Ocean Booking form.
 * Each location has a type (PLC/POL/POD/PLD), name, UN location code,
 * and facility code.
 *
 * Location type labels:
 * - PLC = Place of Receipt
 * - POL = Port of Load
 * - POD = Port of Discharge
 * - PLD = Place of Delivery
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { getFieldIssues } from '../../utils/confidenceUtils';

/**
 * Severity color map for inline issue indicators on select fields
 */
const SEVERITY_COLORS = {
  error:   { border: 'border-l-red-500',   text: 'text-red-600',   icon: 'text-red-500' },
  warning: { border: 'border-l-amber-500', text: 'text-amber-600', icon: 'text-amber-500' },
  info:    { border: 'border-l-blue-500',  text: 'text-blue-600',  icon: 'text-blue-500' },
};

function formatIssueType(type) {
  if (!type) return 'Issue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function IssueIndicator({ issues = [] }) {
  if (issues.length === 0) return null;
  const worst = issues.some(i => i.severity === 'error') ? 'error'
    : issues.some(i => i.severity === 'warning') ? 'warning' : 'info';
  return (
    <span className="relative group/issues inline-flex">
      <svg className={`w-3.5 h-3.5 ${SEVERITY_COLORS[worst]?.icon || 'text-amber-500'} cursor-help`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/issues:block w-64">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {issues.map((issue, idx) => (
            <div key={idx} className={`px-3 py-2 border-l-4 ${SEVERITY_COLORS[issue.severity]?.border || 'border-l-gray-300'} ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
              <p className={`text-xs font-semibold ${SEVERITY_COLORS[issue.severity]?.text || 'text-gray-600'}`}>{formatIssueType(issue.type)}</p>
              {issue.detail && <p className="text-xs text-gray-600 mt-0.5">{issue.detail}</p>}
            </div>
          ))}
        </div>
      </div>
    </span>
  );
}

/**
 * Location type options with human-readable labels
 */
const LOCATION_TYPES = [
  { value: 'PLC', label: 'PLC - Place of Receipt' },
  { value: 'POL', label: 'POL - Port of Load' },
  { value: 'POD', label: 'POD - Port of Discharge' },
  { value: 'PLD', label: 'PLD - Place of Delivery' }
];

/**
 * Map location type codes to readable labels for display
 */
const LOCATION_TYPE_LABELS = {
  PLC: 'Place of Receipt',
  POL: 'Port of Load',
  POD: 'Port of Discharge',
  PLD: 'Place of Delivery'
};

/**
 * OBLocationsFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function OBLocationsFields({ control, register, isEditMode, issues = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'shipmentLocations'
  });

  /**
   * Add a new empty location
   */
  const addLocation = () => {
    append({
      locationType: 'POL',
      locationName: '',
      UNLocationCode: '',
      facilityCode: ''
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">
          Shipment Locations
        </h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addLocation}>
            + Add Location
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No locations added yet. Click "Add Location" to specify ports and places.
        </div>
      )}

      {/* Location entries */}
      {fields.map((field, index) => (
        <div
          key={field.id}
          className="border border-gray-200 rounded-lg p-4 space-y-3"
        >
          {/* Location header with type badge and remove button */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {LOCATION_TYPE_LABELS[field.locationType] || `Location #${index + 1}`}
            </span>
            {isEditMode && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            )}
          </div>

          {/* Location fields grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Location Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="inline-flex items-center gap-1">
                  Location Type
                  <IssueIndicator issues={getFieldIssues(issues, `shipmentLocations[${index}].locationType`)} />
                </span>
              </label>
              <select
                {...register(`shipmentLocations.${index}.locationType`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {LOCATION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Name */}
            <Input
              label="Location Name"
              {...register(`shipmentLocations.${index}.locationName`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `shipmentLocations[${index}].locationName`)}
              placeholder="e.g., Shanghai Port"
            />

            {/* UN Location Code */}
            <Input
              label="UN Location Code"
              {...register(`shipmentLocations.${index}.UNLocationCode`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `shipmentLocations[${index}].UNLocationCode`)}
              placeholder="e.g., CNSHA"
            />

            {/* Facility Code */}
            <Input
              label="Facility Code"
              {...register(`shipmentLocations.${index}.facilityCode`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `shipmentLocations[${index}].facilityCode`)}
              placeholder="e.g., APMT"
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default OBLocationsFields;
